import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { H1Title, Paragraph } from "../../components/typography/typography";
import JudgingService from "../../services/judgingService";
import CompetitionService from "../../services/competitionService";
import { Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faCheckCircle, faClock, faGavel } from "@fortawesome/free-solid-svg-icons";

const STATUS_ICON = {
  pending: { icon: faClock, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  in_progress: { icon: faFileAlt, color: 'text-primary', bg: 'bg-primary-bg' },
  completed: { icon: faCheckCircle, color: 'text-green', bg: 'bg-green-light' },
};

export default function JudgeDashboard() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  // null until we know the competition's active round — judges score round1/round2 only,
  // so anything past round 2 clamps to round2 (the latest scoreable round).
  const [round, setRound] = useState(null);

  useEffect(() => {
    CompetitionService.getActive()
      .then((res) => {
        const n = res.data?.data?.competition?.currentRound || 1;
        setRound(n >= 2 ? 'round2' : 'round1');
      })
      .catch(() => setRound('round1'));
  }, []);

  useEffect(() => { if (round) loadAssignments(); }, [round]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const { data } = await JudgingService.getMyAssignments(round);
      setAssignments(data.data.assignments || []);
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to load assignments');
    } finally { setLoading(false); }
  };

  const stats = {
    total: assignments.length,
    pending: assignments.filter(a => a.status === 'pending').length,
    inProgress: assignments.filter(a => a.status === 'in_progress').length,
    completed: assignments.filter(a => a.status === 'completed').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <H1Title size="sm">Judge Dashboard</H1Title>
          <Paragraph size="sm" className="text-grey-667 mt-1">Review and score assigned submissions</Paragraph>
        </div>
        <select value={round || 'round1'} onChange={(e) => setRound(e.target.value)} className="border border-grey-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="round1">Round 1 — Proposals</option>
          <option value="round2">Round 2 — Pitches</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-grey-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={faClock} className="text-orange-500" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.pending}</p>
            <p className="text-xs text-grey-667">Pending</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-bg rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={faGavel} className="text-primary" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.inProgress}</p>
            <p className="text-xs text-grey-667">In Progress</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-light rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.completed}</p>
            <p className="text-xs text-grey-667">Scored</p>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-grey-667">Loading...</div>
        ) : assignments.length === 0 ? (
          <div className="p-12 text-center text-grey-667">No assignments yet for this round</div>
        ) : (
          <div className="divide-y divide-grey-200">
            {assignments.map((a) => {
              const sub = a.submissionId;
              const si = STATUS_ICON[a.status] || STATUS_ICON.pending;
              return (
                <div key={a._id} className="flex items-center justify-between px-5 py-4 hover:bg-grey-bg/50">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${si.bg}`}>
                      <FontAwesomeIcon icon={si.icon} className={si.color} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{sub?.submitterId?.firstName} {sub?.submitterId?.lastName}</p>
                      <p className="text-xs text-grey-98">{sub?.teamId?.name || 'Individual'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${si.bg} ${si.color}`}>
                      {a.status.replace('_', ' ')}
                    </span>
                    <Link
                      to={`/judge/submissions/${sub?._id}/score?round=${round}`}
                      className="text-sm text-primary hover:underline font-medium"
                    >
                      {a.status === 'completed' ? 'View Score' : 'Score'}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
